import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  getServiceOrder, createServiceOrder, updateServiceOrder, updateServiceOrderItem,
  getClients, getServices, getWorkers, convertOrderToInvoice, createClient, createWorker,
  updateServiceItemWorkers
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  ArrowLeft, Save, ClipboardList, FileText, FileCheck, GitBranch, 
  Plus, Trash2, Users, UserCheck, CheckCircle2, Clock, AlertCircle, Building2, User
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getOrderStatusClass } from '@/lib/utils';
import OrderDocumentChecklist from '@/components/OrderDocumentChecklist';
import OrderStatusHistory from '@/components/OrderStatusHistory';

const statuses = ['Draft', 'Pending', 'Documents Collected', 'Submitted', 'In Progress', 'Completed', 'Delivered', 'Cancelled', 'On Hold'];
const priorities = ['Normal', 'Urgent', 'VIP'];
const itemStatuses = ['Pending', 'In Progress', 'Completed'];

export default function ServiceOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  // Form state for new orders
  const [order, setOrder] = useState({
    client_id: searchParams.get('client_id') || '',
    order_workers: [],  // Order-level workers
    items: [],  // Service line items
    priority: 'Normal',
    notes: '',
    govt_fee_paid_by: 'Client',  // "Client" or "Us"
    auto_create_invoice: true
  });
  
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [allWorkers, setAllWorkers] = useState([]);  // All workers for the selected client
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [workerDialogOpen, setWorkerDialogOpen] = useState(false);
  const [itemStatusDialogOpen, setItemStatusDialogOpen] = useState(false);
  const [selectedItemForStatus, setSelectedItemForStatus] = useState(null);

  // Worker management dialog
  const [workerMgmtDialogOpen, setWorkerMgmtDialogOpen] = useState(false);
  const [selectedItemForWorkers, setSelectedItemForWorkers] = useState(null);
  const [itemWorkers, setItemWorkers] = useState([]);
  const [savingWorkers, setSavingWorkers] = useState(false);

  // Inline creation dialogs
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);
  const [newWorkerDialogOpen, setNewWorkerDialogOpen] = useState(false);
  const [newClientForm, setNewClientForm] = useState({
    client_type: 'Company',
    trade_name: '',
    full_name: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    emirate: 'Dubai'
  });
  const [newWorkerForm, setNewWorkerForm] = useState({
    full_name: '',
    passport_number: '',
    nationality: '',
    phone: ''
  });
  const [creatingClient, setCreatingClient] = useState(false);
  const [creatingWorker, setCreatingWorker] = useState(false);

  const loadWorkers = useCallback(async (clientId) => {
    try {
      const res = await getWorkers({ client_id: clientId, limit: 100 });
      setAllWorkers(res.data.items);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [clientsRes, servicesRes] = await Promise.all([
        getClients({ limit: 1000 }),
        getServices({ limit: 200 })
      ]);
      setClients(clientsRes.data.items);
      setServices(servicesRes.data.items);

      if (!isNew && id) {
        setLoading(true);
        const orderRes = await getServiceOrder(id);
        setOrder(orderRes.data);
        
        // Load workers for this client
        if (orderRes.data.client_id) {
          loadWorkers(orderRes.data.client_id);
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, loadWorkers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (order.client_id) {
      loadWorkers(order.client_id);
    }
  }, [order.client_id, loadWorkers]);

  const handleChange = (field, value) => {
    setOrder((prev) => ({ ...prev, [field]: value }));
  };

  // Handle inline client creation
  const handleCreateClient = async () => {
    if (!newClientForm.trade_name && !newClientForm.full_name) {
      toast.error('Please enter company or individual name');
      return;
    }
    
    setCreatingClient(true);
    try {
      const payload = {
        client_type: newClientForm.client_type,
        emirate: newClientForm.emirate || 'Dubai',
        contact_person: newClientForm.contact_person || null,
        contact_phone: newClientForm.contact_phone || null,
        contact_email: newClientForm.contact_email || null
      };
      
      if (newClientForm.client_type === 'Company') {
        payload.trade_name = newClientForm.trade_name;
      } else {
        payload.full_name = newClientForm.full_name || newClientForm.trade_name;
      }
      
      const res = await createClient(payload);
      
      // Add to clients list and select it
      setClients(prev => [...prev, res.data]);
      handleChange('client_id', res.data.id);
      
      const clientName = res.data.display_name || newClientForm.trade_name || newClientForm.full_name || 'New client';
      toast.success(`Client "${clientName}" created and selected!`);
      setNewClientDialogOpen(false);
      setNewClientForm({ 
        client_type: 'Company', 
        trade_name: '', 
        full_name: '',
        contact_person: '', 
        contact_phone: '', 
        contact_email: '',
        emirate: 'Dubai'
      });
    } catch (error) {
      const errMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Failed to create client';
      toast.error(errMsg);
      console.error('Create client error:', error.response?.data);
    } finally {
      setCreatingClient(false);
    }
  };

  // Handle inline worker creation
  const handleCreateWorker = async () => {
    if (!newWorkerForm.full_name) {
      toast.error('Please enter worker name');
      return;
    }
    if (!order.client_id) {
      toast.error('Please select a client first');
      return;
    }
    
    setCreatingWorker(true);
    try {
      const res = await createWorker({
        ...newWorkerForm,
        client_id: order.client_id
      });
      
      // Add to workers list
      setAllWorkers(prev => [...prev, res.data]);
      
      const workerName = res.data.full_name || newWorkerForm.full_name || 'New worker';
      toast.success(`Worker "${workerName}" created!`);
      setNewWorkerDialogOpen(false);
      setNewWorkerForm({ full_name: '', passport_number: '', nationality: '', phone: '' });
    } catch (error) {
      const errMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Failed to create worker';
      toast.error(errMsg);
    } finally {
      setCreatingWorker(false);
    }
  };

  // Open worker management dialog for a service item
  const openWorkerManagement = (item) => {
    setSelectedItemForWorkers(item);
    // Initialize with existing assigned workers or empty array
    setItemWorkers(item.assigned_workers || []);
    setWorkerMgmtDialogOpen(true);
  };

  // Add a worker to the service item
  const addWorkerToItem = () => {
    setItemWorkers(prev => [...prev, { worker_id: '', worker_name: '', quantity: 1, notes: '' }]);
  };

  // Remove a worker from the service item
  const removeWorkerFromItem = (index) => {
    setItemWorkers(prev => prev.filter((_, i) => i !== index));
  };

  // Update a worker assignment
  const updateWorkerAssignment = (index, field, value) => {
    setItemWorkers(prev => {
      const updated = [...prev];
      if (field === 'worker_id') {
        const worker = allWorkers.find(w => w.id === value);
        updated[index] = { 
          ...updated[index], 
          worker_id: value, 
          worker_name: worker?.full_name || '' 
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });
  };

  // Save worker assignments
  const saveWorkerAssignments = async () => {
    if (!selectedItemForWorkers) return;
    
    // Validate total quantity doesn't exceed item quantity
    const totalAssigned = itemWorkers.reduce((sum, w) => sum + (parseInt(w.quantity) || 0), 0);
    if (totalAssigned > selectedItemForWorkers.quantity) {
      toast.error(`Total assigned (${totalAssigned}) exceeds service quantity (${selectedItemForWorkers.quantity})`);
      return;
    }
    
    // Filter out empty worker assignments
    const validAssignments = itemWorkers.filter(w => w.worker_id && w.quantity > 0);
    
    setSavingWorkers(true);
    try {
      await updateServiceItemWorkers(id, selectedItemForWorkers.item_id, {
        assigned_workers: validAssignments.map(w => ({
          worker_id: w.worker_id,
          quantity: parseInt(w.quantity) || 1,
          notes: w.notes || null
        }))
      });
      toast.success('Workers assigned successfully');
      setWorkerMgmtDialogOpen(false);
      loadData(); // Refresh order data
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Failed to assign workers';
      toast.error(errMsg);
    } finally {
      setSavingWorkers(false);
    }
  };

  // Get remaining unassigned quantity for an item
  const getRemainingQuantity = () => {
    if (!selectedItemForWorkers) return 0;
    const totalAssigned = itemWorkers.reduce((sum, w) => sum + (parseInt(w.quantity) || 0), 0);
    return selectedItemForWorkers.quantity - totalAssigned;
  };

  // Add service item
  const handleAddItem = (service) => {
    const newItem = {
      temp_id: Date.now().toString(),  // Temporary ID for new items
      service_id: service.id,
      service_name: service.name,
      service_code: service.service_code,
      quantity: 1,
      govt_fee: service.govt_fee_variable ? '' : service.govt_fee,
      service_fee: service.service_fee_default,
      assigned_worker_id: '',
      notes: ''
    };
    setOrder(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setItemDialogOpen(false);
  };

  // Update item field
  const handleItemChange = (index, field, value) => {
    setOrder(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  // Remove item
  const handleRemoveItem = (index) => {
    setOrder(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Remove item from existing order (with confirmation)
  const handleRemoveItemExisting = async (index) => {
    if (!window.confirm('Are you sure you want to remove this service from the order?')) return;
    
    const updatedItems = order.items.filter((_, i) => i !== index);
    setOrder(prev => ({ ...prev, items: updatedItems }));
    
    // Auto-save after removal
    try {
      const apiItems = updatedItems.map(item => ({
        id: item.item_id || null,  // Preserve item ID for invoice links
        service_id: item.service_id,
        quantity: parseInt(item.quantity) || 1,
        govt_fee: parseFloat(item.govt_fee) || null,
        service_fee: parseFloat(item.service_fee) || null,
        assigned_worker_id: item.assigned_worker_id || null,
        notes: item.notes || null
      }));
      
      await updateServiceOrder(id, { items: apiItems });
      toast.success('Service removed from order');
      loadData(); // Refresh to get updated totals
    } catch (error) {
      toast.error('Failed to remove service');
      loadData(); // Revert on error
    }
  };

  // Add item to existing order
  const handleAddItemExisting = async (service) => {
    const newItem = {
      service_id: service.id,
      service_name: service.name,
      service_code: service.service_code,
      quantity: 1,
      govt_fee: service.govt_fee_variable ? 0 : service.govt_fee,
      service_fee: service.service_fee_default,
      assigned_worker_id: '',
      notes: ''
    };
    
    const updatedItems = [...order.items, newItem];
    setOrder(prev => ({ ...prev, items: updatedItems }));
    setItemDialogOpen(false);
    setEditingItem(null);
    
    // Auto-save after addition
    try {
      const apiItems = updatedItems.map(item => ({
        id: item.item_id || null,  // Preserve item ID for invoice links
        service_id: item.service_id,
        quantity: parseInt(item.quantity) || 1,
        govt_fee: parseFloat(item.govt_fee) || null,
        service_fee: parseFloat(item.service_fee) || null,
        assigned_worker_id: item.assigned_worker_id || null,
        notes: item.notes || null
      }));
      
      await updateServiceOrder(id, { items: apiItems });
      toast.success('Service added to order');
      loadData(); // Refresh to get updated totals
    } catch (error) {
      toast.error('Failed to add service');
      loadData(); // Revert on error
    }
  };

  // Update existing item
  const handleUpdateItemExisting = async (index, updatedItem) => {
    const updatedItems = [...order.items];
    updatedItems[index] = { ...updatedItems[index], ...updatedItem };
    setOrder(prev => ({ ...prev, items: updatedItems }));
    setItemDialogOpen(false);
    setEditingItem(null);
    
    // Auto-save after update
    try {
      const apiItems = updatedItems.map(item => ({
        id: item.item_id || null,  // Preserve item ID for invoice links
        service_id: item.service_id,
        quantity: parseInt(item.quantity) || 1,
        govt_fee: parseFloat(item.govt_fee) || null,
        service_fee: parseFloat(item.service_fee) || null,
        assigned_worker_id: item.assigned_worker_id || null,
        notes: item.notes || null
      }));
      
      await updateServiceOrder(id, { items: apiItems });
      toast.success('Service updated');
      loadData(); // Refresh to get updated totals
    } catch (error) {
      toast.error('Failed to update service');
      loadData(); // Revert on error
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let vatTotal = 0;
    
    order.items.forEach(item => {
      const govtFee = parseFloat(item.govt_fee) || 0;
      const serviceFee = parseFloat(item.service_fee) || 0;
      const quantity = parseInt(item.quantity) || 1;
      const itemSubtotal = (govtFee + serviceFee) * quantity;
      const itemVat = itemSubtotal * 0.05;  // 5% VAT
      subtotal += itemSubtotal;
      vatTotal += itemVat;
    });
    
    return { subtotal, vatTotal, total: subtotal + vatTotal };
  };

  const handleSave = async () => {
    if (!order.client_id) {
      toast.error('Please select a client');
      return;
    }
    if (order.items.length === 0) {
      toast.error('Please add at least one service');
      return;
    }
    
    setSaving(true);
    try {
      // Prepare items for API
      const apiItems = order.items.map(item => ({
        id: item.item_id || null,  // Include existing item ID to preserve invoice links
        service_id: item.service_id,
        quantity: parseInt(item.quantity) || 1,
        govt_fee: parseFloat(item.govt_fee) || null,
        service_fee: parseFloat(item.service_fee) || null,
        assigned_worker_id: item.assigned_worker_id || null,
        notes: item.notes || null
      }));

      if (isNew) {
        const res = await createServiceOrder({
          client_id: order.client_id,
          order_workers: order.order_workers,
          items: apiItems,
          priority: order.priority,
          notes: order.notes,
          govt_fee_paid_by: order.govt_fee_paid_by,
          auto_create_invoice: order.auto_create_invoice
        });
        toast.success('Order created successfully');
        navigate(`/service-orders/${res.data.id}`);
      } else {
        await updateServiceOrder(id, {
          ...order,
          items: apiItems
        });
        toast.success('Order updated successfully');
        loadData();  // Refresh data
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  // Update individual item status (for worker tracking)
  const handleUpdateItemStatus = async (itemId, updates) => {
    try {
      await updateServiceOrderItem(id, itemId, updates);
      toast.success('Item updated');
      loadData();  // Refresh
      setItemStatusDialogOpen(false);
      setSelectedItemForStatus(null);
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  const totals = calculateTotals();

  // ============= NEW ORDER FORM =============
  if (isNew) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/service-orders')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">New Service Order</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Client & Priority */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Order Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Client *</Label>
                    <div className="flex gap-2">
                      <Select value={order.client_id} onValueChange={(v) => handleChange('client_id', v)}>
                        <SelectTrigger data-testid="order-client" className="flex-1">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setNewClientDialogOpen(true)}
                        title="Create New Client"
                        data-testid="create-client-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={order.priority} onValueChange={(v) => handleChange('priority', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {priorities.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Government Fee Payment & Auto Invoice */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="text-sm font-medium">Government Fees Paid By</Label>
                    <Select value={order.govt_fee_paid_by} onValueChange={(v) => handleChange('govt_fee_paid_by', v)}>
                      <SelectTrigger data-testid="govt-fee-paid-by">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Client">Client (Include in Invoice)</SelectItem>
                        <SelectItem value="Us">Us (Deduct from Visa Card)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {order.govt_fee_paid_by === 'Us' 
                        ? 'Govt fees will be recorded as expense from Visa Card' 
                        : 'Govt fees will be included in client invoice'}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 pt-6">
                    <input
                      type="checkbox"
                      id="auto-invoice"
                      checked={order.auto_create_invoice}
                      onChange={(e) => handleChange('auto_create_invoice', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="auto-invoice" className="text-sm cursor-pointer">
                      Auto-create invoice on order creation
                    </Label>
                  </div>
                </div>

                {/* Order-level Workers */}
                {order.client_id && (
                  <div>
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Order Workers (Shared Pool)
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Assign workers at order level. You can also assign specific workers per service below.
                    </p>
                    <div className="flex gap-2">
                      <Select 
                        value="" 
                        onValueChange={(v) => {
                          if (v && !order.order_workers.includes(v)) {
                            handleChange('order_workers', [...order.order_workers, v]);
                          }
                        }}
                      >
                        <SelectTrigger data-testid="order-workers" className="flex-1">
                          <SelectValue placeholder="Add worker to order" />
                        </SelectTrigger>
                        <SelectContent>
                          {allWorkers.filter(w => !order.order_workers.includes(w.id)).map((w) => (
                            <SelectItem key={w.id} value={w.id}>{w.full_name}</SelectItem>
                          ))}
                          {allWorkers.filter(w => !order.order_workers.includes(w.id)).length === 0 && (
                            <div className="text-sm text-muted-foreground p-2 text-center">
                              No workers available
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setNewWorkerDialogOpen(true)}
                        title="Create New Worker"
                        data-testid="create-worker-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {order.order_workers.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {order.order_workers.map(workerId => {
                          const worker = allWorkers.find(w => w.id === workerId);
                          return (
                            <Badge key={workerId} variant="secondary" className="flex items-center gap-1">
                              {worker?.full_name || workerId}
                              <button
                                type="button"
                                className="ml-1 hover:text-destructive"
                                onClick={() => handleChange('order_workers', order.order_workers.filter(id => id !== workerId))}
                              >
                                ×
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Service Items */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Services</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setItemDialogOpen(true)}
                  disabled={!order.client_id}
                  data-testid="add-service-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Service
                </Button>
              </CardHeader>
              <CardContent>
                {order.items.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No services added yet</p>
                    <p className="text-sm">Click "Add Service" to add services to this order</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {order.items.map((item, index) => (
                      <div 
                        key={item.temp_id || item.item_id || index} 
                        className="border rounded-lg p-4 space-y-3"
                        data-testid={`service-item-${index}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{item.service_code}</Badge>
                            <span className="font-medium">{item.service_name}</span>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleRemoveItem(index)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <Label className="text-xs">Quantity</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Govt Fee (AED)</Label>
                            <Input
                              type="number"
                              value={item.govt_fee}
                              onChange={(e) => handleItemChange(index, 'govt_fee', e.target.value)}
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Service Fee (AED)</Label>
                            <Input
                              type="number"
                              value={item.service_fee}
                              onChange={(e) => handleItemChange(index, 'service_fee', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Line Total</Label>
                            <div className="h-10 flex items-center font-mono text-sm">
                              {formatCurrency(
                                ((parseFloat(item.govt_fee) || 0) + (parseFloat(item.service_fee) || 0)) 
                                * (parseInt(item.quantity) || 1)
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Per-service worker assignment */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs flex items-center gap-1">
                              <UserCheck className="w-3 h-3" />
                              Assigned Worker (Optional)
                            </Label>
                            <Select 
                              value={item.assigned_worker_id || 'none'} 
                              onValueChange={(v) => handleItemChange(index, 'assigned_worker_id', v === 'none' ? '' : v)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select worker" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No worker assigned</SelectItem>
                                {allWorkers.map((w) => (
                                  <SelectItem key={w.id} value={w.id}>{w.full_name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Notes</Label>
                            <Input
                              value={item.notes || ''}
                              onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                              placeholder="Optional notes for this service"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Order Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={order.notes || ''}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={3}
                  placeholder="General notes for this order..."
                />
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent>
                {order.items.length > 0 ? (
                  <div className="space-y-3">
                    <div className="text-sm text-muted-foreground">
                      {order.items.length} service{order.items.length > 1 ? 's' : ''} selected
                    </div>
                    <div className="border-t pt-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span className="font-mono">{formatCurrency(totals.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">VAT (5%)</span>
                        <span className="font-mono">{formatCurrency(totals.vatTotal)}</span>
                      </div>
                      <div className="border-t pt-2 flex justify-between">
                        <span className="font-semibold">Total</span>
                        <span className="font-mono font-semibold text-lg">{formatCurrency(totals.total)}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Add services to see summary
                  </p>
                )}

                <Button 
                  className="w-full mt-6" 
                  onClick={handleSave} 
                  disabled={saving || order.items.length === 0}
                  data-testid="save-order-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Creating...' : 'Create Order'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Add Service Dialog */}
        <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Select Service</DialogTitle>
              <DialogDescription>
                Choose a service to add to this order
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 cursor-pointer"
                  onClick={() => handleAddItem(service)}
                  data-testid={`service-option-${service.id}`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{service.service_code}</Badge>
                      <span className="font-medium">{service.name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Govt: {formatCurrency(service.govt_fee)} | Service: {formatCurrency(service.service_fee_default)}
                    </p>
                  </div>
                  <div className="text-right font-mono text-sm">
                    {formatCurrency((service.govt_fee || 0) + (service.service_fee_default || 0))}
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Inline Create Client Dialog */}
        <Dialog open={newClientDialogOpen} onOpenChange={setNewClientDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Create New Client
              </DialogTitle>
              <DialogDescription>
                Quickly add a new client without leaving this page
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>Client Type</Label>
                <Select 
                  value={newClientForm.client_type} 
                  onValueChange={(v) => setNewClientForm(p => ({ ...p, client_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Company">Company</SelectItem>
                    <SelectItem value="Individual">Individual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{newClientForm.client_type === 'Company' ? 'Trade/Company Name *' : 'Full Name *'}</Label>
                <Input
                  value={newClientForm.client_type === 'Company' ? newClientForm.trade_name : newClientForm.full_name}
                  onChange={(e) => setNewClientForm(p => ({ 
                    ...p, 
                    [newClientForm.client_type === 'Company' ? 'trade_name' : 'full_name']: e.target.value 
                  }))}
                  placeholder={newClientForm.client_type === 'Company' ? 'ABC Trading LLC' : 'John Smith'}
                  autoFocus
                />
              </div>
              {newClientForm.client_type === 'Company' && (
                <div>
                  <Label>Contact Person</Label>
                  <Input
                    value={newClientForm.contact_person}
                    onChange={(e) => setNewClientForm(p => ({ ...p, contact_person: e.target.value }))}
                    placeholder="Contact name"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={newClientForm.contact_phone}
                    onChange={(e) => setNewClientForm(p => ({ ...p, contact_phone: e.target.value }))}
                    placeholder="+971 50 123 4567"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={newClientForm.contact_email}
                    onChange={(e) => setNewClientForm(p => ({ ...p, contact_email: e.target.value }))}
                    placeholder="email@example.com"
                    type="email"
                  />
                </div>
              </div>
              <div>
                <Label>Emirate</Label>
                <Select 
                  value={newClientForm.emirate} 
                  onValueChange={(v) => setNewClientForm(p => ({ ...p, emirate: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Dubai">Dubai</SelectItem>
                    <SelectItem value="Abu Dhabi">Abu Dhabi</SelectItem>
                    <SelectItem value="Sharjah">Sharjah</SelectItem>
                    <SelectItem value="Ajman">Ajman</SelectItem>
                    <SelectItem value="RAK">RAK</SelectItem>
                    <SelectItem value="Fujairah">Fujairah</SelectItem>
                    <SelectItem value="UAQ">UAQ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewClientDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateClient} disabled={creatingClient}>
                <Plus className="w-4 h-4 mr-2" />
                {creatingClient ? 'Creating...' : 'Create & Select'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Inline Create Worker Dialog */}
        <Dialog open={newWorkerDialogOpen} onOpenChange={setNewWorkerDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Create New Worker
              </DialogTitle>
              <DialogDescription>
                Add a new worker for the selected client
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={newWorkerForm.full_name}
                  onChange={(e) => setNewWorkerForm(p => ({ ...p, full_name: e.target.value }))}
                  placeholder="Worker full name"
                  autoFocus
                />
              </div>
              <div>
                <Label>Passport Number</Label>
                <Input
                  value={newWorkerForm.passport_number}
                  onChange={(e) => setNewWorkerForm(p => ({ ...p, passport_number: e.target.value }))}
                  placeholder="AB1234567"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nationality</Label>
                  <Input
                    value={newWorkerForm.nationality}
                    onChange={(e) => setNewWorkerForm(p => ({ ...p, nationality: e.target.value }))}
                    placeholder="e.g. Indian"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={newWorkerForm.phone}
                    onChange={(e) => setNewWorkerForm(p => ({ ...p, phone: e.target.value }))}
                    placeholder="+971 50 123 4567"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewWorkerDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateWorker} disabled={creatingWorker}>
                <Plus className="w-4 h-4 mr-2" />
                {creatingWorker ? 'Creating...' : 'Create Worker'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============= DETAIL VIEW =============
  const handleConvertToInvoice = async () => {
    if (!window.confirm('Convert this order to an invoice?')) return;
    try {
      const res = await convertOrderToInvoice(id);
      toast.success('Invoice created: ' + res.data.invoice_number);
      navigate(`/invoices/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleStatusChange = (newStatus) => {
    setOrder(prev => ({ ...prev, status: newStatus }));
  };

  const getItemStatusIcon = (status) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'In Progress':
        return <Clock className="w-4 h-4 text-amber-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getItemStatusBadge = (status) => {
    switch (status) {
      case 'Completed':
        return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
      case 'In Progress':
        return <Badge className="bg-amber-100 text-amber-700">In Progress</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/service-orders')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <ClipboardList className="w-6 h-6 text-primary" />
              <h1 className="text-2xl font-bold">{order.order_number}</h1>
              <Badge className={getOrderStatusClass(order.status)}>{order.status}</Badge>
              <Badge variant={order.priority === 'Urgent' ? 'destructive' : 'secondary'}>{order.priority}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">Created {formatDate(order.created_at)}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {order.status === 'Completed' && !order.invoice_id && (
            <Button variant="outline" onClick={handleConvertToInvoice} data-testid="convert-to-invoice-btn">
              <FileText className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            Update
          </Button>
        </div>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList>
          <TabsTrigger value="details" className="gap-2">
            <ClipboardList className="w-4 h-4" /> Details
          </TabsTrigger>
          <TabsTrigger value="services" className="gap-2">
            <FileCheck className="w-4 h-4" /> Services ({order.items?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <FileCheck className="w-4 h-4" /> Documents
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <GitBranch className="w-4 h-4" /> History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Order Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Client</p>
                      <p className="font-medium">{order.client?.display_name || order.client_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Reference No</p>
                      <p className="font-mono">{order.reference_number || '-'}</p>
                    </div>
                    {order.invoice_numbers && order.invoice_numbers.length > 0 ? (
                      <div>
                        <p className="text-muted-foreground">Invoices ({order.invoice_count || order.invoice_numbers.length})</p>
                        <div className="flex flex-wrap gap-1">
                          {order.invoice_numbers.map((invNum, idx) => (
                            <Button 
                              key={invNum} 
                              variant="link" 
                              className="p-0 h-auto text-sm" 
                              onClick={() => navigate(`/invoices/${order.invoice_ids[idx]}`)}
                            >
                              {invNum}{idx < order.invoice_numbers.length - 1 ? ',' : ''}
                            </Button>
                          ))}
                        </div>
                      </div>
                    ) : order.invoice_number && (
                      <div>
                        <p className="text-muted-foreground">Invoice</p>
                        <Button variant="link" className="p-0 h-auto" onClick={() => navigate(`/invoices/${order.invoice_id}`)}>
                          {order.invoice_number}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Order-level Workers */}
              {order.order_workers_detailed && order.order_workers_detailed.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Order Workers
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {order.order_workers_detailed.map(worker => (
                        <Badge key={worker.id} variant="secondary" className="py-1.5 px-3">
                          <UserCheck className="w-3 h-3 mr-1" />
                          {worker.full_name}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Pricing Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Services</span>
                      <span>{order.item_count || order.items?.length || 1}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Govt Fees</span>
                      <span className="font-mono">{formatCurrency(order.govt_fee)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Service Fees</span>
                      <span className="font-mono">{formatCurrency(order.service_fee)}</span>
                    </div>
                    <div className="border-t pt-3 flex justify-between">
                      <span className="font-semibold">Subtotal</span>
                      <span className="font-mono font-semibold">{formatCurrency(order.total_amount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">VAT</span>
                      <span className="font-mono">{formatCurrency(order.vat_amount)}</span>
                    </div>
                    <div className="border-t pt-3 flex justify-between">
                      <span className="font-semibold">Total</span>
                      <span className="font-mono font-semibold text-lg">{formatCurrency(order.grand_total)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {order.notes && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-sm">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm whitespace-pre-wrap">{order.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="services" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Service Items & Worker Tracking</CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setItemDialogOpen(true)}
                data-testid="add-service-existing-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Service
              </Button>
            </CardHeader>
            <CardContent>
              {order.items && order.items.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service</TableHead>
                      <TableHead className="text-center">Qty</TableHead>
                      <TableHead className="text-right">Govt Fee</TableHead>
                      <TableHead className="text-right">Service Fee</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Assigned Workers</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Completed By</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {order.items.map((item, index) => (
                      <TableRow key={item.item_id || index} data-testid={`order-item-row-${index}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">{item.service_code}</Badge>
                            <span className="font-medium">{item.service_name}</span>
                          </div>
                          {item.notes && (
                            <p className="text-xs text-muted-foreground mt-1">{item.notes}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-center">{item.quantity}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(item.govt_fee)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(item.service_fee)}</TableCell>
                        <TableCell className="text-right font-mono font-medium">{formatCurrency(item.total)}</TableCell>
                        <TableCell>
                          {item.assigned_workers && item.assigned_workers.length > 0 ? (
                            <div className="space-y-1">
                              {item.assigned_workers.map((w) => (
                                <div key={w.worker_id || w.worker_name} className="flex items-center gap-1">
                                  <Badge variant="secondary" className="text-xs">
                                    <UserCheck className="w-3 h-3 mr-1" />
                                    {w.worker_name} ({w.quantity})
                                  </Badge>
                                </div>
                              ))}
                              {(() => {
                                const assigned = item.assigned_workers.reduce((sum, w) => sum + (w.quantity || 0), 0);
                                const remaining = item.quantity - assigned;
                                return remaining > 0 ? (
                                  <p className="text-xs text-amber-600">{remaining} unassigned</p>
                                ) : null;
                              })()}
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs"
                                onClick={() => openWorkerManagement(item)}
                                data-testid={`manage-workers-${index}-btn`}
                              >
                                <Users className="w-3 h-3 mr-1" />
                                Manage
                              </Button>
                            </div>
                          ) : item.assigned_worker_name ? (
                            <div className="space-y-1">
                              <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                                <UserCheck className="w-3 h-3" />
                                {item.assigned_worker_name}
                              </Badge>
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs"
                                onClick={() => openWorkerManagement(item)}
                                data-testid={`manage-workers-${index}-btn`}
                              >
                                <Users className="w-3 h-3 mr-1" />
                                Manage Workers
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openWorkerManagement(item)}
                              data-testid={`assign-workers-${index}-btn`}
                            >
                              <Users className="w-3 h-3 mr-1" />
                              Assign Workers
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>{getItemStatusBadge(item.status)}</TableCell>
                        <TableCell>
                          {item.completed_by_name ? (
                            <div>
                              <Badge variant="outline" className="flex items-center gap-1 w-fit">
                                <CheckCircle2 className="w-3 h-3 text-green-500" />
                                {item.completed_by_name}
                              </Badge>
                              {item.completed_at && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {formatDate(item.completed_at)}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingItem({ ...item, index });
                                setItemDialogOpen(true);
                              }}
                              data-testid={`edit-item-${index}-btn`}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedItemForStatus(item);
                                setItemStatusDialogOpen(true);
                              }}
                              data-testid={`update-item-${index}-btn`}
                            >
                              Status
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleRemoveItemExisting(index)}
                              data-testid={`delete-item-${index}-btn`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No services in this order</p>
                  <p className="text-sm mt-2">Click "Add Service" to add services</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <OrderDocumentChecklist orderId={id} />
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <OrderStatusHistory 
            orderId={id} 
            currentStatus={order.status}
            onStatusChange={handleStatusChange}
          />
        </TabsContent>
      </Tabs>

      {/* Update Item Status Dialog */}
      <Dialog open={itemStatusDialogOpen} onOpenChange={setItemStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Service Item</DialogTitle>
            <DialogDescription>
              {selectedItemForStatus?.service_name}
            </DialogDescription>
          </DialogHeader>
          {selectedItemForStatus && (
            <div className="space-y-4">
              <div>
                <Label>Status</Label>
                <Select 
                  value={selectedItemForStatus.status || 'Pending'} 
                  onValueChange={(v) => setSelectedItemForStatus(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {itemStatuses.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assigned Worker</Label>
                <Select 
                  value={selectedItemForStatus.assigned_worker_id || 'none'} 
                  onValueChange={(v) => setSelectedItemForStatus(prev => ({ ...prev, assigned_worker_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select worker" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No worker assigned</SelectItem>
                    {allWorkers.map((w) => (
                      <SelectItem key={w.id} value={w.id}>{w.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Completed By</Label>
                <Select 
                  value={selectedItemForStatus.completed_by || 'none'} 
                  onValueChange={(v) => setSelectedItemForStatus(prev => ({ ...prev, completed_by: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select worker who completed" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not completed yet</SelectItem>
                    {allWorkers.map((w) => (
                      <SelectItem key={w.id} value={w.id}>{w.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemStatusDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleUpdateItemStatus(selectedItemForStatus.item_id, {
                status: selectedItemForStatus.status,
                assigned_worker_id: selectedItemForStatus.assigned_worker_id === 'none' ? null : (selectedItemForStatus.assigned_worker_id || null),
                completed_by: selectedItemForStatus.completed_by === 'none' ? null : (selectedItemForStatus.completed_by || null)
              })}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Service Dialog (for existing orders) */}
      <Dialog open={itemDialogOpen} onOpenChange={(open) => {
        setItemDialogOpen(open);
        if (!open) setEditingItem(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Service Item' : 'Add Service'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Update the service details' : 'Choose a service to add to this order'}
            </DialogDescription>
          </DialogHeader>
          
          {editingItem ? (
            // Edit Mode
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <Badge variant="outline">{editingItem.service_code}</Badge>
                <span className="font-medium">{editingItem.service_name}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editingItem.quantity}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, quantity: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Government Fee (AED)</Label>
                  <Input
                    type="number"
                    value={editingItem.govt_fee}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, govt_fee: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Service Fee (AED)</Label>
                  <Input
                    type="number"
                    value={editingItem.service_fee}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, service_fee: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Assigned Worker</Label>
                  <Select 
                    value={editingItem.assigned_worker_id || 'none'} 
                    onValueChange={(v) => setEditingItem(prev => ({ ...prev, assigned_worker_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select worker" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No worker assigned</SelectItem>
                      {allWorkers.map((w) => (
                        <SelectItem key={w.id} value={w.id}>{w.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Input
                  value={editingItem.notes || ''}
                  onChange={(e) => setEditingItem(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes for this service"
                />
              </div>
              
              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-sm">
                  <span className="text-muted-foreground">Line Total: </span>
                  <span className="font-mono font-semibold">
                    {formatCurrency(
                      ((parseFloat(editingItem.govt_fee) || 0) + (parseFloat(editingItem.service_fee) || 0)) 
                      * (parseInt(editingItem.quantity) || 1)
                    )}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setItemDialogOpen(false); setEditingItem(null); }}>
                    Cancel
                  </Button>
                  <Button onClick={() => handleUpdateItemExisting(editingItem.index, editingItem)}>
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            // Add Mode - Service Selection
            <div className="space-y-2">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 cursor-pointer"
                  onClick={() => handleAddItemExisting(service)}
                  data-testid={`service-option-${service.id}`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{service.service_code}</Badge>
                      <span className="font-medium">{service.name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Govt: {formatCurrency(service.govt_fee)} | Service: {formatCurrency(service.service_fee_default)}
                    </p>
                  </div>
                  <div className="text-right font-mono text-sm">
                    {formatCurrency((service.govt_fee || 0) + (service.service_fee_default || 0))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Inline Create Client Dialog */}
      <Dialog open={newClientDialogOpen} onOpenChange={setNewClientDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Create New Client
            </DialogTitle>
            <DialogDescription>
              Quickly add a new client without leaving this page
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Client Type</Label>
              <Select 
                value={newClientForm.client_type} 
                onValueChange={(v) => setNewClientForm(p => ({ ...p, client_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Company">Company</SelectItem>
                  <SelectItem value="Individual">Individual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{newClientForm.client_type === 'Company' ? 'Company Name *' : 'Full Name *'}</Label>
              <Input
                value={newClientForm.company_name}
                onChange={(e) => setNewClientForm(p => ({ ...p, company_name: e.target.value }))}
                placeholder={newClientForm.client_type === 'Company' ? 'ABC Trading LLC' : 'John Smith'}
                autoFocus
              />
            </div>
            {newClientForm.client_type === 'Company' && (
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={newClientForm.contact_person}
                  onChange={(e) => setNewClientForm(p => ({ ...p, contact_person: e.target.value }))}
                  placeholder="Contact name"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Phone</Label>
                <Input
                  value={newClientForm.phone}
                  onChange={(e) => setNewClientForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+971 50 123 4567"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  value={newClientForm.email}
                  onChange={(e) => setNewClientForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="email@example.com"
                  type="email"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewClientDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateClient} disabled={creatingClient}>
              <Plus className="w-4 h-4 mr-2" />
              {creatingClient ? 'Creating...' : 'Create & Select'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline Create Worker Dialog */}
      <Dialog open={newWorkerDialogOpen} onOpenChange={setNewWorkerDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Create New Worker
            </DialogTitle>
            <DialogDescription>
              Add a new worker for the selected client
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={newWorkerForm.full_name}
                onChange={(e) => setNewWorkerForm(p => ({ ...p, full_name: e.target.value }))}
                placeholder="Worker full name"
                autoFocus
              />
            </div>
            <div>
              <Label>Passport Number</Label>
              <Input
                value={newWorkerForm.passport_number}
                onChange={(e) => setNewWorkerForm(p => ({ ...p, passport_number: e.target.value }))}
                placeholder="AB1234567"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nationality</Label>
                <Input
                  value={newWorkerForm.nationality}
                  onChange={(e) => setNewWorkerForm(p => ({ ...p, nationality: e.target.value }))}
                  placeholder="e.g. Indian"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={newWorkerForm.phone}
                  onChange={(e) => setNewWorkerForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+971 50 123 4567"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewWorkerDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateWorker} disabled={creatingWorker}>
              <Plus className="w-4 h-4 mr-2" />
              {creatingWorker ? 'Creating...' : 'Create Worker'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Worker Management Dialog */}
      <Dialog open={workerMgmtDialogOpen} onOpenChange={setWorkerMgmtDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Assign Workers to Service
            </DialogTitle>
            <DialogDescription>
              {selectedItemForWorkers && (
                <>
                  Assign workers to <strong>{selectedItemForWorkers.service_name}</strong>
                  <span className="ml-2 text-primary">(Qty: {selectedItemForWorkers.quantity})</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Summary bar */}
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Total Quantity</p>
                  <p className="text-lg font-semibold">{selectedItemForWorkers?.quantity || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Assigned</p>
                  <p className="text-lg font-semibold text-primary">
                    {itemWorkers.reduce((sum, w) => sum + (parseInt(w.quantity) || 0), 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Remaining</p>
                  <p className={`text-lg font-semibold ${getRemainingQuantity() > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    {getRemainingQuantity()}
                  </p>
                </div>
              </div>
              <Button size="sm" onClick={addWorkerToItem} variant="outline" data-testid="add-worker-assignment-btn">
                <Plus className="w-4 h-4 mr-1" />
                Add Worker
              </Button>
            </div>
            
            {/* Worker assignments list */}
            {itemWorkers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No workers assigned yet</p>
                <p className="text-sm mt-1">Click "Add Worker" to start assigning</p>
              </div>
            ) : (
              <div className="space-y-3">
                {itemWorkers.map((wa, idx) => (
                  <div key={wa.worker_id || `wa-${idx}`} className="flex items-start gap-3 p-3 border rounded-lg bg-background" data-testid={`worker-assignment-${idx}`}>
                    <div className="flex-1 grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Worker</Label>
                        <Select
                          value={wa.worker_id}
                          onValueChange={(v) => updateWorkerAssignment(idx, 'worker_id', v)}
                        >
                          <SelectTrigger data-testid={`worker-select-${idx}`}>
                            <SelectValue placeholder="Select worker" />
                          </SelectTrigger>
                          <SelectContent>
                            {allWorkers.map(worker => (
                              <SelectItem key={worker.id} value={worker.id}>
                                {worker.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          max={selectedItemForWorkers?.quantity || 100}
                          value={wa.quantity}
                          onChange={(e) => updateWorkerAssignment(idx, 'quantity', parseInt(e.target.value) || 1)}
                          data-testid={`worker-qty-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Notes</Label>
                        <Input
                          value={wa.notes || ''}
                          onChange={(e) => updateWorkerAssignment(idx, 'notes', e.target.value)}
                          placeholder="Optional notes"
                          data-testid={`worker-notes-${idx}`}
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeWorkerFromItem(idx)}
                      className="text-destructive hover:text-destructive mt-5"
                      data-testid={`remove-worker-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setWorkerMgmtDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveWorkerAssignments} 
              disabled={savingWorkers}
              data-testid="save-worker-assignments-btn"
            >
              {savingWorkers ? 'Saving...' : 'Save Assignments'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
